import { useEffect, useId, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import type { Rect } from '../utils/popoverPlacement';
import { EventPreview } from './EventPreview';
import { useEventPreviewLayer } from './EventPreviewLayer';

interface EventPreviewHostProps {
  event: CalendarEvent | null;
}

type AnchorMeasurement =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'measured'; rect: Rect }
  | { status: 'unavailable' };

const IDLE: AnchorMeasurement = { status: 'idle' };

// Shown purely by whether `event` is non-null; useEventPreview owns
// open/close, this component never opens or closes itself. Inside an
// EventPreviewLayer the preview is handed to the layer, which draws it above
// the whole grid — outside the month cell's overflow:hidden, above sibling
// blocks, and clamped to the visible bounds. The in-place popover below the
// trigger remains only as the fallback when no layer is mounted or the
// anchor cannot be measured.
export function EventPreviewHost({ event }: Readonly<EventPreviewHostProps>) {
  const layer = useEventPreviewLayer();
  const key = useId();
  const anchorRef = useRef<View>(null);
  const [measurement, setMeasurement] = useState<AnchorMeasurement>(IDLE);

  useEffect(() => {
    if (!layer || !event) {
      setMeasurement((current) => (current.status === 'idle' ? current : IDLE));
      return;
    }
    let cancelled = false;
    setMeasurement({ status: 'pending' });
    layer.measureAnchor(anchorRef.current).then((rect) => {
      if (cancelled) return;
      setMeasurement(rect ? { status: 'measured', rect } : { status: 'unavailable' });
    });
    return () => {
      cancelled = true;
    };
  }, [layer, event]);

  useEffect(() => {
    if (!layer || !event || measurement.status !== 'measured') return;
    layer.present(key, event, measurement.rect);
    return () => layer.dismiss(key);
  }, [layer, event, measurement, key]);

  const showInline = event !== null && (!layer || measurement.status === 'unavailable');

  return (
    <>
      <View ref={anchorRef} pointerEvents="none" style={StyleSheet.absoluteFill} />
      {showInline && (
        <View
          testID={`event_preview_inline_${event.id}`}
          pointerEvents="none"
          style={styles.inlinePopover}
        >
          <EventPreview event={event} />
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  inlinePopover: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: tokens.spacing.xs,
    zIndex: 50,
  },
});
